import _ from 'underscore';
import { recommendationConfigs } from './recommendation-configs';
import {
  ESTIMATE_TYPES,
  SUMMARY_LEVELS,
  RECOMMENDATION_TYPES,
  VISUAL_TYPES,
  CHANNEL_ALL_RECOMMENDATIONS,
  CHANNEL_TYPES,
  MAX_NUMBER_OF_RECOMMENDATION_SAMPLES,
  MAX_NUMBER_OF_RECOMMENDATION_SAMPLES_WITH_OS,
} from './consts';
import {
  formatEntityNameForHtml,
  formatSummary,
  formatCost,
  formatPercent,
  formatPercentHundredths,
  truncateEntityName,
  truncateSingleColumnEntityURL,
  formatDecimal,
  formatVisualDataChart,
  formatCompetitionSampleVisualDataTable,
  formatLocaledSourceType,
  formatKeyword,
  getCompetitionSampleInsightTitle,
  getModelForCompetitionSampleAction,
  formatInteger,
} from './formatter/index';

import { getEditorialErrorMessage } from './util';

const formatInsight = (type, sample, entityName, adGroupName, i18n) => {
  let insight = '';
  switch (type) {
    case RECOMMENDATION_TYPES.COMPETITIVE_BID: {
      const insightKey = sample.isHighConverting ?
        `${recommendationConfigs[type].sampleInsight}_High_Converting` :
        recommendationConfigs[type].sampleInsight;
      insight = i18n.getString(
        insightKey,
        {
          keyword: entityName,
          ad_group: adGroupName,
        }
      );
      break;
    }
    case RECOMMENDATION_TYPES.DEVICE_BID_BOOST:
    case RECOMMENDATION_TYPES.LOCATION_BID_BOOST: {
      insight = i18n.getString(
        recommendationConfigs[type].sampleInsight,
        {
          delta: formatPercent(sample.delta, i18n),
          segment: sample.segment,
          competitor_domain: sample.competitor,
        }
      );
      break;
    }
    case RECOMMENDATION_TYPES.SYNDICATION: {
      insight = i18n.getString(
        recommendationConfigs[type].sampleInsight,
        {
          entity_name: entityName,
        }
      );
      break;
    }
    case RECOMMENDATION_TYPES.BUDGET:
    case RECOMMENDATION_TYPES.NEW_KEYWORD: // deprecated in Oct 2023
    case RECOMMENDATION_TYPES.NEW_LOCATION:
    default: {
      if (recommendationConfigs[type].sampleInsight) {
        insight = i18n.getString(recommendationConfigs[type].sampleInsight);
      }
      break;
    }
  }

  return insight;
};

const ONE_COLUMN_IN_TABLE = 'one-column-in-table';
const TWO_COLUMNS_IN_TABLE = 'two-columns-in-table';
const THREE_COLUMNS_IN_TABLE = 'three-columns-in-table';
const FOUR_COLUMNS_IN_TABLE = 'four-columns-in-table';

const formatSample = ({
  type,
  level,
  scope,
  recommendationsCount,
  estimates,
  sample,
  i18n,
  newI18n,
  currency,
  isOptimizationScoreOn,
}) => {
  let insightTitle = '';

  // what you can do
  let actionTitle;
  let actions;
  let moreRecommendations;
  let viewRecommendations;
  let apply;

  // entity
  const entityName = sample.target && formatEntityNameForHtml(level, sample.target);
  const adGroupName = level === SUMMARY_LEVELS.KEYWORD
    && sample.target
    && formatEntityNameForHtml(SUMMARY_LEVELS.ADGROUP, sample.target);
  const isCompetition = CHANNEL_ALL_RECOMMENDATIONS[CHANNEL_TYPES.COMPETITION].indexOf(type) !== -1;

  // visual
  const { visualType } = recommendationConfigs[type];
  let { visualData } = sample;
  visualData = formatVisualDataChart(i18n, visualType, visualData);

  if (isCompetition) {
    insightTitle = getCompetitionSampleInsightTitle({
      type,
      i18n,
      entityName,
      recommendationsCount,
      sample,
    });

    // Action title and action
    ({ actionTitle, actions } = getModelForCompetitionSampleAction({
      type,
      i18n,
      recommendationsCount,
      level,
      entityName,
      currency,
      sample,
    }));

    if (visualType === VISUAL_TYPES.TABLE) {
      visualData = formatCompetitionSampleVisualDataTable({
        type,
        visualData,
        visualTableHeaders: recommendationConfigs[type].visualTableHeaders,
        i18n,
        currency,
      });
    }
  } else if (!recommendationConfigs[type].visualTableScope || !scope
    || _.contains(recommendationConfigs[type].visualTableScope, scope)) {
    let headers = [];
    if (visualType === VISUAL_TYPES.TABLE) {
      if (recommendationConfigs[type].useNewI18n === true) {
        headers = _.map(recommendationConfigs[type].visualTableHeaders, headerKey =>
          newI18n.getString(headerKey));
      } else {
        headers = _.map(recommendationConfigs[type].visualTableHeaders, headerKey =>
          i18n.getString(headerKey));
      }
    } else if (visualType === VISUAL_TYPES.ADS_PREVIEW) {
      headers = _.map(recommendationConfigs[type].visualTableHeaders, headerKey =>
        newI18n.getString(headerKey));
    }

    const values = [];
    let highlights = [];
    let alignments;
    let columnWidths;
    let maxNumberOfRecommendationSamples = isOptimizationScoreOn ?
      MAX_NUMBER_OF_RECOMMENDATION_SAMPLES_WITH_OS : MAX_NUMBER_OF_RECOMMENDATION_SAMPLES;

    if (type === RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS) {
      maxNumberOfRecommendationSamples = MAX_NUMBER_OF_RECOMMENDATION_SAMPLES;
    }

    switch (type) {
      case RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            formatKeyword(row.recommendedKeyword, row.matchType),
            formatLocaledSourceType(i18n, row.recommendedSource),
          ]);
        });
        highlights = [false, true];
        break;
      }
      case RECOMMENDATION_TYPES.TRENDING_QUERY: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            formatKeyword(row.recommendedKeyword, row.matchType),
            {
              nullableArrow: true,
              value: `↑ ${_.isString(row.searchVolumeIncrease) ? row.searchVolumeIncrease : formatPercentHundredths(row.searchVolumeIncrease, i18n)}`,
            },
          ]);
        });
        highlights = [false, true];
        break;
      }
      case RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS: {
        _.map(_.first(sample.visualData[0].searchExamples, maxNumberOfRecommendationSamples), (example) => { // eslint-disable-line max-len
          values.push([example]);
        });
        if (_.size(sample.visualData[0].searchExamples) > maxNumberOfRecommendationSamples) {
          values.push(['...']);
        }

        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.SITE_LINK:
      case RECOMMENDATION_TYPES.CALLOUT: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: TWO_COLUMNS_IN_TABLE,
              value: row.campaignName,
            },
            `↑ ${formatPercentHundredths(row.ctrLift, i18n)}`,
          ]);
        });
        highlights = [false, true];
        break;
      }
      case RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY:
      case RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY: {
        const headerKey = estimates.primaryEstimate === ESTIMATE_TYPES.CONVERSIONS ?
          'Summary_Card_Action_Header_Est_Conv' : 'Summary_Card_Action_Header_Est_Clicks';
        headers.push(i18n.getString(headerKey));
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: TWO_COLUMNS_IN_TABLE,
              value: row.entityName,
            },
            `+${estimates.primaryEstimate === ESTIMATE_TYPES.CONVERSIONS ?
              formatDecimal(row.convIncrease, i18n) : formatDecimal(row.clicksIncrease, i18n)}`,
          ]);
        });
        highlights = [false, true];
        break;
      }
      case RECOMMENDATION_TYPES.REPAIR_ADS:
      case RECOMMENDATION_TYPES.REPAIR_KEYWORD: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: ONE_COLUMN_IN_TABLE,
              value: row.adGroupName,
            },
          ]);
        });
        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            row.recommendedKeyword,
          ]);
        });
        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.REMOVE_CONFLICTING_NEGATIVE_KEYWORD: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            formatKeyword(row.negativeKeyword, row.matchType),
            formatKeyword(row.blockedKeyword, row.blockedKeywordMatchType),
          ]);
        });
        highlights = [false, false];
        break;
      }
      case RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: ONE_COLUMN_IN_TABLE,
              value: row.adGroupName,
            },
          ]);
        });
        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.REALLOCATE_BUDGET: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            formatCost(row.suggestBudget, i18n, i18n.currency),
            {
              enabledToolTip: true,
              toolTipClass: FOUR_COLUMNS_IN_TABLE,
              value: row.fromCampaign,
            },
            {
              enabledToolTip: true,
              toolTipClass: FOUR_COLUMNS_IN_TABLE,
              value: row.toCampaign,
            },
            `+${formatDecimal(row.clicksIncrease, i18n)}`,
          ]);
        });
        alignments = ['right', 'left', 'left', 'right'];
        highlights = [false, false, false, true];
        columnWidths = ['19%', '27%', '27%', '27%'];
        break;
      }
      case RECOMMENDATION_TYPES.FIX_AD_DESTINATION: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            truncateSingleColumnEntityURL(row.UrlOrLandingPage),
          ]);
        });
        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.FIX_AD_TEXT: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            truncateEntityName(getEditorialErrorMessage(row.policyCode)),
            formatInteger(row.numberRejectedAds, i18n),
            formatInteger(row.numberRejectedKeywords, i18n),
          ]);
        });
        highlights = [false, false, false];
        columnWidths = ['50%', '25%', '25%'];
        break;
      }
      case RECOMMENDATION_TYPES.MULTIMEDIA_ADS: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: ONE_COLUMN_IN_TABLE,
              value: row.adGroupName,
            },
          ]);
        });
        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.IMAGE_EXTENSION: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: TWO_COLUMNS_IN_TABLE,
              value: row.campaignName,
            },
            `+${formatDecimal(row.clicksIncrease, i18n)}`,
          ]);
        });
        highlights = [false, true];
        break;
      }
      case RECOMMENDATION_TYPES.REPAIR_MISSING_KEYWORD_PARAMS: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            row.keyword,
            `+${formatDecimal(row.impressionsIncrease, i18n)}`,
          ]);
        });
        highlights = [false, true];
        break;
      }
      case RECOMMENDATION_TYPES.FIX_NO_IMPRESSION_BID: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            formatKeyword(row.keyword, row.matchType),
            formatCost(row.suggestedBid, i18n, i18n.currency),
            {
              nullableArrow: true,
              value: row.impressionsIncrease === 0 ?
                null : `+${formatInteger(row.impressionsIncrease, i18n)}`,
            },
          ]);
        });
        highlights = [false, false, true];
        columnWidths = ['50%', '25%', '25%'];
        break;
      }
      case RECOMMENDATION_TYPES.REPAIR_UNTARGETED_OFFER: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: THREE_COLUMNS_IN_TABLE,
              value: row.campaignName,
            },
            `+${formatDecimal(row.targetedProductsIncrease, i18n)}`,
            {
              nullableArrow: true,
              value: row.impressionsIncrease === 0 ?
                null : `+${formatInteger(row.impressionsIncrease, i18n)}`,
            },
          ]);
        });
        highlights = [false, true, true];
        columnWidths = ['50%', '25%', '25%'];
        break;
      }
      case RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push(row);
        });
        break;
      }
      case RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: ONE_COLUMN_IN_TABLE,
              value: row.campaignName,
            },
          ]);
        });
        highlights = [false];
        break;
      }
      case RECOMMENDATION_TYPES.SYNDICATION_GAP: {
        _.map(_.first(sample.visualData, maxNumberOfRecommendationSamples), (row) => {
          values.push([
            {
              enabledToolTip: true,
              toolTipClass: THREE_COLUMNS_IN_TABLE,
              value: row.campaignName,
            },
            `+${formatDecimal(row.clicksIncrease, i18n)}`,
            `+${formatDecimal(row.convIncrease, i18n)}`,
          ]);
        });
        highlights = [false, true, true];
        break;
      }
      default: {
        break;
      }
    }
    if (_.size(sample.visualData) > maxNumberOfRecommendationSamples
      && type !== RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS
      && type !== RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS) {
      values.push(_.map(headers, () => '...'));
    }

    if (_.isEmpty(values)) {
      headers.pop();
    }

    visualData = {
      headers,
      values,
      highlights,
      alignments,
      columnWidths,
    };
  }

  // insight
  const insight = formatInsight(type, sample, entityName, adGroupName, i18n);

  if (recommendationsCount > 1) {
    moreRecommendations = i18n.getString('Summary_Card_More_Recommendations');
    viewRecommendations = i18n.getString(
      'Summary_Card_View_Recommendations_Multi',
      { n: i18n.formatDecimalToInteger(recommendationsCount) }
    );
    apply = i18n.getString('Summary_Card_Apply_All');
  } else {
    moreRecommendations = null;
    viewRecommendations = i18n.getString('Summary_Card_View_Recommendations');
    apply = i18n.getString('Button_Apply');
  }

  return {
    sample: {
      insightTitle,
      insight,
      visualType,
      visualData,
      actionTitle,
      actions,
      moreRecommendations,
    },
    viewRecommendations,
    apply,
  };
};

/**
 * @param {Object} options - options for formatting summary model
 * @param {string} options.type - recommendation type
 * @param {string} options.level - target level of the recommendation.
 * For example, Device bid boost and Location bid boost can target Campaign or AdGroup.
 * @param {string} options.scope - current scope user selected in wunderbar
 * @param {Object} options.sample - sample data for summary card (insight, actions)
 * @param {number} options.recommendationsCount - total count of recommendations
 * @param {boolean} options.isHighConverting - if the ALL recommendations for this type
 * is HighConverting.
 * @param {string} options.currency - currency for current account
 * @param {Object} options.i18n - Localization component
 * @return {Object} formatted model for React component to render
 */
export const getSummaryCardModel = (options) => {
  const summary = formatSummary(options);
  const sample = formatSample(options);

  return _.extend(
    {
      summary,
      type: options.type,
      level: options.level,
    },
    sample
  );
};
